import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { getDocumentFileMeta } from '../../animal-details-documents/document-file-type.util';
import { MEDICAL_DOCUMENT_CATEGORIES } from '../../models/animal-document-category.enum';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';
import { MedicalFile } from '../../states/medical/animal-medical.selectors';

/** Lesesicht: alle Dateien, die an Karteikarten hängen. */
@Component({
  selector: 'app-medical-attachments',
  templateUrl: './medical-attachments.component.html',
  styleUrls: ['../medical-shared.scss'],
  standalone: false
})
export class MedicalAttachmentsComponent implements OnInit, OnDestroy {
  @Output() goToRecord = new EventEmitter<number>();
  @Output() openFile = new EventEmitter<MedicalFile>();

  readonly categories: string[] = [...MEDICAL_DOCUMENT_CATEGORIES, 'image', 'video'];

  files: MedicalFile[] = [];
  search = '';
  sortOrder: 'newest' | 'oldest' = 'newest';
  categoryFilter: string | null = null;
  readonly sortOptions = [
    { value: 'newest', label: 'animalMedical.attachments.sortNewest' },
    { value: 'oldest', label: 'animalMedical.attachments.sortOldest' },
  ];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _translateService: TranslocoService
  ) {}

  ngOnInit(): void {
    this._medicalRepository.files$.pipe(takeUntil(this._destroy$)).subscribe(files => this.files = files);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  categoryLabelKey(category: string): string {
    if (category === 'image') return 'animalMedical.attachments.image';
    if (category === 'video') return 'animalMedical.attachments.video';
    return `animal.documentCategory.${category}`;
  }

  countFor(category: string): number {
    return this.files.filter(file => file.category === category).length;
  }

  toggleCategory(category: string): void {
    this.categoryFilter = this.categoryFilter === category ? null : category;
  }

  get visibleFiles(): MedicalFile[] {
    const term = this.search.trim().toLowerCase().replace(/^#/, '');
    const filtered = this.files.filter(file => {
      if (this.categoryFilter && file.category !== this.categoryFilter) return false;
      if (!term) return true;
      const category = this._translateService.translate(this.categoryLabelKey(file.category)).toLowerCase();
      return file.name.toLowerCase().includes(term)
        || category.includes(term)
        || String(file.recordId) === term;
    });
    const direction = this.sortOrder === 'newest' ? -1 : 1;
    return filtered.sort((a, b) => direction * ((a.date ?? '').localeCompare(b.date ?? '') || a.id - b.id));
  }

  fileIcon(file: MedicalFile): string {
    if (file.kind === 'video') return 'pi pi-video text-gray-500';
    const meta = getDocumentFileMeta(file.name);
    return `${meta.icon} ${meta.colorClass}`;
  }
}
